import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils/cn";
import { Spinner } from "./Spinner";
import { EmptyState } from "./EmptyState";

interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  className?: string;
  align?: "left" | "right" | "center";
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField: (row: T) => string;
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  /** Icon shown above the empty-state title (e.g. a module-appropriate glyph). */
  emptyIcon?: ReactNode;
  /** RBAC-gated next-step action rendered under the empty-state text - e.g.
   * an "+ Add X" button when the caller can create one, a "Clear Filters"
   * button when the empty result is due to search/filters rather than a
   * truly empty dataset. Callers decide which to pass based on their own
   * filter/permission state. */
  emptyAction?: ReactNode;
  onRowClick?: (row: T) => void;
  /** Extra attributes for one row's <tr> - e.g. the highlight and scroll
   * anchor a deep-linked record needs (see features/performance's
   * deepLinkRowProps). Its `className` is merged with the row's own hover
   * styling rather than replacing it. */
  rowProps?: (row: T) => (HTMLAttributes<HTMLTableRowElement> & Record<`data-${string}`, string | undefined>) | undefined;
}

/**
 * A plain, dependency-light table for the common case (render each row from
 * a column spec). Pages with rich sort/filter interaction reach for
 * @tanstack/react-table directly instead of extending this one.
 */
export function DataTable<T>({
  columns,
  data,
  keyField,
  loading,
  emptyTitle = "No records found",
  emptyDescription,
  emptyIcon,
  emptyAction,
  onRowClick,
  rowProps,
}: DataTableProps<T>) {
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50/70">
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  "whitespace-nowrap px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500",
                  col.align === "right" && "text-right",
                  col.align === "center" && "text-center",
                  col.className,
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="py-16 text-center">
                <Spinner className="mx-auto h-6 w-6 text-primary-600" />
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>
                <EmptyState icon={emptyIcon} title={emptyTitle} description={emptyDescription} action={emptyAction} />
              </td>
            </tr>
          ) : (
            data.map((row) => {
              const extra = rowProps?.(row);
              const { className: extraClassName, ...extraAttrs } = extra ?? {};
              return (
              <tr
                key={keyField(row)}
                {...extraAttrs}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                // Every row gets the theme-tinted hover now (was previously
                // gated behind onRowClick) - the tint signals "this row is
                // highlighted," the cursor separately signals "this row is
                // actionable," matching the interaction spec's own
                // distinction between the two. Strengthened 2026-08-18 (v2):
                // a visibly stronger tint (was /40) plus a left-edge accent
                // via inset box-shadow rather than `border-l` - a <tr>'s own
                // border is unreliable under border-collapse across
                // browsers, box-shadow renders consistently. `group` lets
                // row-action icon buttons (wrapped in .group-hover:opacity-
                // 100 below) stay dim at rest and sharpen on hover, per the
                // "increase prominence only when the row is hovered"
                // requirement.
                //
                // Strengthened again 2026-08-19 (v3): added a full-perimeter
                // inset outline (same inset-box-shadow technique as the
                // left-edge accent, for the same border-collapse reason) so
                // a selectable row gets the same "outlined on hover" cue the
                // KPI/module cards now use, not just a directional edge bar.
                className={cn(
                  "group transition-colors duration-200 hover:bg-primary-50/70 hover:shadow-[inset_0_0_0_1.5px_theme(colors.primary.200),inset_3px_0_0_0_theme(colors.primary.400)]",
                  onRowClick && "cursor-pointer",
                  extraClassName,
                )}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cn(
                      "whitespace-nowrap px-4 py-3 text-slate-700",
                      col.align === "right" && "text-right",
                      col.align === "center" && "text-center",
                      col.className,
                    )}
                  >
                    {col.render(row)}
                  </td>
                ))}
              </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
